import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Users, Briefcase, Info, ArrowRight } from 'lucide-react';

const FeatureCards = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState([false, false, false]);
  const cardRefs = [useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    const observers = cardRefs.map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible((prev) => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
            }
          });
        },
        { threshold: 0.1 }
      );

      if (ref.current) {
        observer.observe(ref.current);
      }

      return observer;
    });

    return () => {
      observers.forEach((observer, index) => {
        if (cardRefs[index].current) {
          observer.unobserve(cardRefs[index].current);
        }
      });
    };
  }, []);

  const features = [
    {
      icon: Briefcase,
      title: 'Need Workers?',
      description: 'Get qualified, WCB-compliant workers in construction, warehouse, food processing, agriculture, transportation, and cold storage — delivered in 24-48 hours.',
      buttonText: 'Request Workers Now',
      action: () => navigate('/employers'),
      bgColor: 'bg-[#003366]',
      gradient: 'from-[#003366] to-[#004488]',
    },
    {
      icon: Users,
      title: 'Looking for Work?',
      description: 'Register with WorkNation and get matched with jobs across BC. Flexible schedules, weekly pay, and real opportunities in 6 industries.',
      buttonText: 'Find Work Now',
      action: () => navigate('/job-seekers'),
      bgColor: 'bg-[#004488]',
      gradient: 'from-[#004488] to-[#0066cc]',
    },
    {
      icon: Info,
      title: 'Why WorkNation?',
      description: 'We handle payroll, WCB, and admin so you don\u2019t have to. 200+ workers placed. No hidden fees. No long-term commitments. Just reliable staffing.',
      buttonText: 'Learn About Us',
      action: () => navigate('/about'),
      bgColor: 'bg-[#003366]',
      gradient: 'from-[#003366] to-[#004488]',
    },
  ];

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-float"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-float" style={{animationDelay: '1.5s'}}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              ref={cardRefs[index]}
              className={`group bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-700 hover:shadow-2xl hover:transform hover:scale-105 relative ${
                isVisible[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Animated gradient background */}
              <div className={`bg-gradient-to-br ${feature.gradient} p-8 text-white transition-all duration-500 relative overflow-hidden`}>
                <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <feature.icon size={48} className="mb-4 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 relative z-10" />
                <h3 className="text-2xl font-bold mb-3 relative z-10">{feature.title}</h3>
              </div>
              <div className="p-8 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <p className="text-gray-700 text-lg mb-6 leading-relaxed relative z-10">{feature.description}</p>
                <Button
                  onClick={feature.action}
                  className="w-full bg-[#003366] hover:bg-[#004488] text-white py-6 text-lg font-semibold transition-all duration-300 shine-effect group relative z-10"
                >
                  {feature.buttonText}
                  <ArrowRight className="ml-2 inline-block group-hover:translate-x-2 transition-transform duration-300" size={20} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeatureCards;